import { useRef, useState } from "react";
import {
  Upload,
  Loader2,
  ScanLine,
  ClipboardEdit,
} from "lucide-react";
import { ACCEPT_TYPES } from "./scoreColor";

const SCAN_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

export default function ResumeUploadZone({
  onFiles,
  uploading,
  onScanResume,
  onScanApplication,
}) {
  const inputRef = useRef(null);
  const scanResumeRef = useRef(null);
  const scanAppRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleChange = (files) => {
    if (files && files.length) onFiles(files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleScan = (e, callback, ref) => {
    const files = e.target.files;
    if (files && files.length && callback) callback(files);
    if (ref?.current) ref.current.value = "";
  };

  return (
    <>
      <label
        htmlFor="resume-file-input"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files) handleChange(e.dataTransfer.files);
        }}
        data-testid="resume-dropzone"
        className={`mt-6 flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-10 text-center transition-colors ${
          dragging
            ? "border-amber-500 bg-amber-100"
            : "border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100"
        }`}
      >
        <Upload className="h-7 w-7 text-slate-400" />
        <div className="mt-4 font-heading text-base font-medium">
          Drop resumes here, or click to select
        </div>
        <div className="mt-1 text-xs text-slate-500">
          PDF, DOCX, or TXT · up to 5 MB each
        </div>
        <input
          ref={inputRef}
          id="resume-file-input"
          data-testid="resume-file-input"
          type="file"
          multiple
          accept={ACCEPT_TYPES}
          className="hidden"
          onChange={(e) => handleChange(e.target.files)}
        />
      </label>

      {/* Camera / scan-from-image options. Mobile devices honour `capture` to
          open the rear camera directly; desktop falls back to a file picker. */}
      {(onScanResume || onScanApplication) && (
        <div
          data-testid="resume-scan-actions"
          className="mt-3 flex flex-wrap items-center gap-2 text-[11px]"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            or scan a paper copy:
          </span>
          {onScanResume && (
            <>
              <button
                type="button"
                data-testid="scan-resume-btn"
                onClick={() => scanResumeRef.current?.click()}
                disabled={uploading > 0}
                title="OCR a photo/scan of a printed or handwritten résumé"
                className="inline-flex items-center gap-1 border border-blue-300 bg-white px-3 py-1.5 font-semibold uppercase tracking-[0.16em] text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                <ScanLine className="h-3.5 w-3.5" />
                Scan résumé
              </button>
              <input
                ref={scanResumeRef}
                data-testid="scan-resume-input"
                type="file"
                multiple
                accept={SCAN_ACCEPT}
                capture="environment"
                className="hidden"
                onChange={(e) => handleScan(e, onScanResume, scanResumeRef)}
              />
            </>
          )}
          {onScanApplication && (
            <>
              <button
                type="button"
                data-testid="scan-application-btn"
                onClick={() => scanAppRef.current?.click()}
                disabled={uploading > 0}
                title="OCR a photo/scan of a paper employment application (handwriting supported)"
                className="inline-flex items-center gap-1 border border-violet-300 bg-white px-3 py-1.5 font-semibold uppercase tracking-[0.16em] text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              >
                <ClipboardEdit className="h-3.5 w-3.5" />
                Scan employment application
              </button>
              <input
                ref={scanAppRef}
                data-testid="scan-application-input"
                type="file"
                multiple
                accept={SCAN_ACCEPT}
                capture="environment"
                className="hidden"
                onChange={(e) => handleScan(e, onScanApplication, scanAppRef)}
              />
            </>
          )}
        </div>
      )}

      {uploading > 0 && (
        <div
          data-testid="resume-uploading"
          className="mt-4 flex items-center gap-3 border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Analyzing {uploading} item{uploading === 1 ? "" : "s"} with AI…
        </div>
      )}
    </>
  );
}
